/**
 * @fileoverview Bulk-edit dialog for firm contacts (MOR-915)
 *
 * Mirrors the precedent library's bulk-edit flow: each editable dimension
 * (category, contact type) sits in a section gated by an "update this"
 * checkbox, so only explicitly enabled dimensions are written. Applies via
 * a single `bulkUpdateFirmContacts` mutation that diff-patches per row.
 */

import { useEffect, useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";
import { Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Label } from "@/ui/primitives/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { roleIconComponent } from "@/components/settings/tasks/role-icons";
import {
  CONTACT_TYPE_META,
  CONTACT_TYPE_ORDER,
  CREATABLE_FIRM_CATEGORIES,
  FIRM_CATEGORY_META,
  type ContactType,
  type FirmContactCategory,
  type FirmContactCustomCategory,
} from "./types";

export function BulkEditFirmContactsDialog({
  open,
  onOpenChange,
  orgId,
  firmContactIds,
  customCategories,
  onApplied,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: Id<"organizations">;
  firmContactIds: ReadonlyArray<string>;
  customCategories: ReadonlyArray<FirmContactCustomCategory>;
  onApplied: () => void;
}) {
  const bulkUpdate = useConvexMutation(
    api.contacts.mutations.bulkUpdateFirmContacts,
  );

  const [applying, setApplying] = useState(false);
  const [updateCategory, setUpdateCategory] = useState(false);
  /** Built-in category or `custom:<id>`. */
  const [categoryValue, setCategoryValue] = useState<string>("bank");
  const [updateType, setUpdateType] = useState(false);
  const [typeValue, setTypeValue] = useState<ContactType>("organisation");

  // Reset the form whenever the dialog closes so a later open starts clean.
  useEffect(() => {
    if (open) return;
    setUpdateCategory(false);
    setCategoryValue("bank");
    setUpdateType(false);
    setTypeValue("organisation");
  }, [open]);

  const hasAnyChange = updateCategory || updateType;
  const total = firmContactIds.length;

  const handleApply = async () => {
    if (!hasAnyChange) return;
    setApplying(true);
    try {
      const patch: {
        category?: FirmContactCategory;
        customCategoryId?: Id<"firmContactCategories"> | null;
        contactType?: ContactType;
      } = {};
      if (updateCategory) {
        if (categoryValue.startsWith("custom:")) {
          // Custom category keeps "other" as the built-in fallback so all
          // legacy read paths remain valid (matches single-row edits).
          patch.category = "other";
          patch.customCategoryId = categoryValue.slice(
            "custom:".length,
          ) as Id<"firmContactCategories">;
        } else {
          patch.category = categoryValue as FirmContactCategory;
          patch.customCategoryId = null;
        }
      }
      if (updateType) {
        patch.contactType = typeValue;
      }
      const { updated } = await bulkUpdate({
        orgId,
        firmContactIds: firmContactIds as Array<Id<"firmContacts">>,
        patch,
      });
      toast.success(
        updated === total
          ? `Updated ${updated} contact${updated === 1 ? "" : "s"}.`
          : `Updated ${updated} of ${total} contacts (the rest already matched).`,
      );
      onOpenChange(false);
      onApplied();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not update contacts."));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Edit {total} contact{total === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Only the sections you tick are changed; everything else on the
            selected contacts is left as-is.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <BulkSection
            title="Category"
            enabled={updateCategory}
            onEnabledChange={setUpdateCategory}
          >
            <Select
              value={categoryValue}
              onValueChange={setCategoryValue}
              disabled={!updateCategory}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CREATABLE_FIRM_CATEGORIES.map((c) => {
                  const meta = FIRM_CATEGORY_META[c];
                  return (
                    <SelectItem key={c} value={c}>
                      <span className="inline-flex items-center gap-2">
                        <meta.icon className="h-3.5 w-3.5" />
                        {meta.label}
                      </span>
                    </SelectItem>
                  );
                })}
                {customCategories.map((c) => {
                  const Icon = roleIconComponent(c.icon) ?? Tag;
                  return (
                    <SelectItem key={c._id} value={`custom:${c._id}`}>
                      <span className="inline-flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5" />
                        {c.name}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </BulkSection>

          <BulkSection
            title="Contact type"
            enabled={updateType}
            onEnabledChange={setUpdateType}
          >
            <Select
              value={typeValue}
              onValueChange={(v) => setTypeValue(v as ContactType)}
              disabled={!updateType}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_TYPE_ORDER.map((t) => {
                  const meta = CONTACT_TYPE_META[t];
                  return (
                    <SelectItem key={t} value={t}>
                      <span className="inline-flex items-center gap-2">
                        <meta.icon className="h-3.5 w-3.5" />
                        {meta.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </BulkSection>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={applying}
          >
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={applying || !hasAnyChange}>
            {applying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Applying…
              </>
            ) : (
              `Apply to ${total} contact${total === 1 ? "" : "s"}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One gated dimension: checkbox toggles whether the section is applied. */
function BulkSection({
  title,
  enabled,
  onEnabledChange,
  children,
}: {
  title: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <label className="mb-2 flex cursor-pointer items-center gap-2">
        <Checkbox
          checked={enabled}
          onCheckedChange={(v) => onEnabledChange(v === true)}
        />
        <Label className="cursor-pointer text-sm font-medium">
          Update {title.toLowerCase()}
        </Label>
      </label>
      {children}
    </div>
  );
}
